import { AccountSummary } from "@homing/core/protocol";
import { useCallback, useEffect, useState } from "react";
import { z } from "zod";
import type { Me } from "./session.ts";

const ACCOUNTS_KEY = "homing.accounts";
const SELECTED_KEY = "homing.account";
const List = z.array(AccountSummary);

function read<T>(key: string, schema: z.ZodType<T>): T | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const r = schema.safeParse(JSON.parse(raw));
    return r.success ? r.data : null;
  } catch {
    return null;
  }
}

function write(key: string, v: unknown) {
  try {
    localStorage.setItem(key, typeof v === "string" ? v : JSON.stringify(v));
  } catch {
    /* private mode */
  }
}

const fromMe = (me: Me): AccountSummary[] =>
  me.accounts.map((a) => ({ id: a.id, email: a.email, provider: "google", displayName: a.email, status: "active" }));

/** GET /api/accounts → AccountSummary[], cached for offline starts. */
export function useAccounts(me: Me): { accounts: AccountSummary[] | null; offline: boolean } {
  const [state, set] = useState<{ accounts: AccountSummary[] | null; offline: boolean }>(() => ({ accounts: read(ACCOUNTS_KEY, List), offline: false }));
  useEffect(() => {
    let live = true;
    fetch("/api/accounts", { credentials: "same-origin" })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const list = List.parse(await r.json());
        write(ACCOUNTS_KEY, list);
        if (live) set({ accounts: list, offline: false });
      })
      .catch(() => {
        if (live) set({ accounts: read(ACCOUNTS_KEY, List) ?? fromMe(me), offline: true });
      });
    return () => {
      live = false;
    };
  }, [me]);
  return state;
}

/**
 * The selected account: `?connected=<id>` (the OAuth return) wins, then the last one
 * used, then the first. The query parameter is removed from the URL once read.
 */
export function useSelectedAccount(accounts: AccountSummary[] | null): [string | null, (id: string) => void] {
  const [selected, setSelected] = useState<string | null>(() => {
    const p = new URLSearchParams(location.search);
    const connected = p.get("connected");
    if (connected) {
      p.delete("connected");
      const qs = p.toString();
      history.replaceState(null, "", location.pathname + (qs ? `?${qs}` : "") + location.hash);
      write(SELECTED_KEY, connected);
      return connected;
    }
    try {
      return localStorage.getItem(SELECTED_KEY);
    } catch {
      return null;
    }
  });
  const choose = useCallback((id: string) => {
    write(SELECTED_KEY, id);
    setSelected(id);
  }, []);
  if (!accounts) return [null, choose];
  const valid = selected && accounts.some((a) => a.id === selected) ? selected : (accounts[0]?.id ?? null);
  return [valid, choose];
}

/** Full-page navigation into the server's Google OAuth flow. */
export function connectGoogle() {
  location.assign("/connect/google/start");
}
